import React, { useEffect, useState } from "react";
import TextArea from "../../components/data_entry/text_area";
import QuestionLabel from "./components/question_label";
import { QuestionProps } from "../survey";
import nextId from "react-id-generator";
import TextareaQuestionWrapper from "../../components/base_ui/higher_order_components/questions/textarea_question_wrapper";

export interface Customizations {
  prompt: string;
  description?: string;
  placeholder?: string;
  optional?: boolean;
}

export type Answer = string;

export default function SurveyQuestionOpenResponse({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
  showValidation,
  showRefreshedComponents = true,
  hackToHideFeedback = false,
}: QuestionProps<Answer, Customizations>) {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [touched, setTouched] = useState<boolean>(false);

  const label = customizations.prompt;
  const id = "open-response-" + nextId();
  const description = customizations.description;
  const placeholder = customizations.placeholder;
  const optional = typeof customizations.optional === "undefined" ? false : customizations.optional;
  const answerIsValid = optional || (typeof currentAnswer !== "undefined" && currentAnswer !== "");

  useEffect(() => {
    setErrorMessage(!answerIsValid ? "Please complete the required field." : undefined);
    setAbleToSubmit(answerIsValid);
  }, [answerIsValid, setAbleToSubmit]);

  const deprecatedOpenResponse = (
    <div className="open-response">
      <QuestionLabel
        label={
          // This is a hack, and intentionally does not try to relabel 'mentorship'
          // in a place where we can't easily determine program id.
          // This hack is in place because the open-response question on the Post-Session Survey
          // leads to some answers from participants where participants/partners expect quick action.
          // However, there is no good process for reviewing PSS responses in a timely manner.
          // It takes PS a lot of manual time to review these responses via exports.
          // Removing a question from the PSS "the right way" is high-effort due to current survey design.
          // This hack keeps the question around but instructs the user to our normal helpdesk flow,
          // and does not allow submission for that question so that we don't have the liability of
          // having to do something with feedback that we cannot quickly and efficiently address.
          hackToHideFeedback
            ? "If there's anything we can do to improve your experience that requires immediate attention (like a change in your mentorship), please email us at help@mentorcollective.org."
            : label
        }
        htmlFor={id}
        description={description}
        // If using the hack, we need to guarantee that the question is optional
        // so that there is never a scenario where the entire survey submission is blocked
        // due to this question being tampered with
        questionIsOptional={hackToHideFeedback ? true : optional}
        data-test="open-response-question-label"
      />
      {hackToHideFeedback ? (
        <></>
      ) : (
        <TextArea
          data-test="open-response-input"
          id={id}
          questionIsOptional={optional}
          placeholder={
            placeholder ? placeholder : optional ? undefined : "Enter n/a if not applicable"
          }
          rows={4}
          onChange={({ currentTarget: { value: updatedResponse } }) =>
            updateAnswer(updatedResponse)
          }
          value={currentAnswer ? currentAnswer : ""}
          error={
            !answerIsValid && showValidation ? "Please complete the required field." : undefined
          }
        />
      )}
    </div>
  );

  const openResponse = (
    <TextareaQuestionWrapper
      dataTest={"textarea-input"}
      id={id}
      label={label}
      secondaryText={description}
      optional={optional}
      placeholder={placeholder}
      value={currentAnswer}
      onChange={({ currentTarget: { value: updatedResponse } }) => {
        updateAnswer(updatedResponse);
      }}
      onBlur={() => {
        setTouched(true);
      }}
      errorMessage={touched || showValidation ? errorMessage : undefined}
    />
  );

  return showRefreshedComponents ? openResponse : deprecatedOpenResponse;
}
